import * as RToast from '@radix-ui/react-toast';
import { CheckCircle2, AlertTriangle, X, Info } from 'lucide-react';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

type ToastTone = 'success' | 'error' | 'info';
type ToastItem = { id: string; title: string; description?: string; tone: ToastTone };

const ToastCtx = createContext<(t: Omit<ToastItem, 'id'>) => void>(() => undefined);

export function useToast() {
  return useContext(ToastCtx);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setItems((prev) => [...prev, { id, ...t }]);
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      <RToast.Provider swipeDirection="right" duration={4000}>
        {children}
        {items.map((t) => (
          <RToast.Root
            key={t.id}
            onOpenChange={(open) => !open && remove(t.id)}
            className="surface shadow-lg p-3 pr-8 relative data-[state=open]:animate-[fade-up_200ms_ease-out]"
          >
            <div className="flex items-start gap-2.5">
              {t.tone === 'success' && <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-500" />}
              {t.tone === 'error' && <AlertTriangle className="w-4 h-4 mt-0.5 text-red-500" />}
              {t.tone === 'info' && <Info className="w-4 h-4 mt-0.5 text-[var(--accent-500)]" />}
              <div className="min-w-0 flex-1">
                <RToast.Title className="text-[13.5px] font-medium">{t.title}</RToast.Title>
                {t.description && (
                  <RToast.Description className="text-[12.5px] muted mt-0.5">
                    {t.description}
                  </RToast.Description>
                )}
              </div>
              <RToast.Close className="absolute top-2 right-2 p-1 rounded hover:bg-[var(--bg-subtle)]" aria-label="Close">
                <X className="w-3.5 h-3.5 subtle" />
              </RToast.Close>
            </div>
          </RToast.Root>
        ))}
        <RToast.Viewport className="fixed bottom-4 right-4 flex flex-col gap-2 w-[360px] max-w-[calc(100vw-2rem)] z-[60] outline-none" />
      </RToast.Provider>
    </ToastCtx.Provider>
  );
}
